import { takeEvery, takeLatest, put } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import {
  getRecommendations,
  getRecommendationsV2,
  getRecommenderConfig,
  recommenderConfigCallComplete,
  saveRecommendations,
  saveRecommendationsFailed,
  saveRecommenderConfig,
} from "../../slices/RecommenderSlice";
import { METHODS } from "../../../data/constants";
import { trackRecommendationsFetched } from "../../../recommendations/analytics";

/**
 * `performance` is declared locally because `packages/core`'s NATIVE tsconfig has no `DOM` lib
 * (it must stay RN-safe), and the global is what both runtimes actually provide — the browser via
 * `lib.dom`, React Native via Hermes. Same treatment as `structuredClone` in
 * `helpers/catalogueHelpers/index.desktop.ts`. A module-scoped declaration shadows the DOM one on
 * web rather than conflicting with it.
 *
 * This is what the previous per-variant file reported as `TS2304 Cannot find name 'performance'`
 * against the mobile baseline; declaring it clears those instead of moving them.
 */
declare const performance: { now(): number };

/**
 * `redux-store/saga/recommender` — the WHOLE domain, SHARED.
 *
 * 🔴 This folder used to be two divergent bodies. As of the Suggestive Selling convergence the
 * WEB body is the one definition and both platforms run it. What the other platform gains:
 *
 * | | before | now |
 * |---|---|---|
 * | V2 saga + response normalisation | absent | shared |
 * | `recommenderConfigCallComplete` in `finally` | absent (no such action) | shared |
 * | stale-fetch cancellation | `takeEvery` — a slow first response could overwrite a newer one | `takeLatest` |
 * | request envelope | bare `action.payload` | `{ dataToPost, skipGlobalLoader }` |
 *
 * The envelope change is the load-bearing one: it is what let this file collapse at all, and it
 * is safe because the flow (`recommendations/engine`) is the only thing that dispatches these
 * actions, so there is exactly one caller to keep in step.
 *
 * ⚠️ `skipGlobalLoader` is honoured by BOTH platforms' request interceptors. Suggestions are an
 * ambient background call; a full-screen spinner over one would block billing.
 *
 * ⚠️ `getRecommendationsV2Saga` fires the SAME event name as V1, with the same params. That is
 * deliberate — the funnel measures "recommendations fetched" regardless of engine version — and
 * is preserved rather than disambiguated.
 *
 * ⚠️ `recommendationEngine: true` is read by `utils/apiService`'s request interceptor to swap the
 * base URL to the recommendation engine. It is one of the apps' own axios keys, which is why the
 * config is cast (`as any`) exactly as the app file casts it.
 *
 * ⚠️ The fetch analytics go through `recommendations/analytics`, which owns the flow's whole GA
 * envelope (event name, category, the 0.1 ms rounding) and reaches the effects bridge itself.
 */
function normalizeRecommendationsV2Response(response: any) {
  const items = response?.items || [];
  return {
    recommendationId: response?.recommendation_id,
    items: items.map((item: any) => item.item_id),
    itemList: items.map((item: any) => ({
      itemId: item.item_id,
      // score: item.score,
      labels: item.labels,
      // contributions: item.contributions,
    })),
  };
}

function* getRecommenderConfigSaga(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: METHODS.GET,
      url: ENDPOINT.RECOMMENDER.getRecommenderConfig(),
    });
    if (!response?.code) {
      yield put(saveRecommenderConfig(response));
    } else {
      yield put(saveRecommenderConfig({}));
    }
  } catch (error) {
    console.log(error);
  } finally {
    yield put(recommenderConfigCallComplete());
  }
}

function* getRecommendationsSaga(action: any): Generator<any, void, any> {
  const startTime = performance.now();
  try {
    const response = yield api.request({
      method: METHODS.POST,
      url: ENDPOINT.RECOMMENDER.getRecommendations(),
      data: action.payload?.dataToPost,
      skipGlobalLoader: action.payload?.skipGlobalLoader,
    } as any);
    if (!response?.code) {
      yield put(saveRecommendations(response));
    } else {
      yield put(saveRecommendations({}));
    }
    const loadTime = performance.now() - startTime;
    trackRecommendationsFetched({
      loadTimeMs: loadTime,
      recommendationId: response?.recommendationId || response?.code,
    });
  } catch (error) {
    yield put(saveRecommendationsFailed({}));
    console.log(error);
    const loadTime = performance.now() - startTime;
    trackRecommendationsFetched({
      loadTimeMs: loadTime,
      recommendationId: "Failed",
    });
  }
}

function* getRecommendationsV2Saga(action: any): Generator<any, void, any> {
  const startTime = performance.now();
  try {
    const response = yield api.request({
      method: METHODS.POST,
      url: ENDPOINT.RECOMMENDER.getRecommendationsV2(),
      data: action.payload?.dataToPost,
      skipGlobalLoader: action.payload?.skipGlobalLoader,
      recommendationEngine: true,
    } as any);
    if (!response?.code) {
      const normalized = normalizeRecommendationsV2Response(response);
      yield put(saveRecommendations(normalized));
      const loadTime = performance.now() - startTime;
      trackRecommendationsFetched({
        loadTimeMs: loadTime,
        recommendationId: normalized.recommendationId,
      });
    } else {
      yield put(saveRecommendations({}));
      const loadTime = performance.now() - startTime;
      trackRecommendationsFetched({
        loadTimeMs: loadTime,
        recommendationId: response?.code,
      });
    }
  } catch (error) {
    yield put(saveRecommendationsFailed({}));
    console.log(error);
    const loadTime = performance.now() - startTime;
    trackRecommendationsFetched({
      loadTimeMs: loadTime,
      recommendationId: "Failed",
    });
  }
}

export default function* root(): any {
  yield takeEvery(getRecommenderConfig.type, getRecommenderConfigSaga); // one-shot login config
  yield takeLatest(getRecommendations.type, getRecommendationsSaga); // cancel stale V1 fetches
  yield takeLatest(getRecommendationsV2.type, getRecommendationsV2Saga); // cancel stale V2 fetches
}
